<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import Button from "@/components/Button.vue";
import Input from "@/components/Input.vue";
import { encryptPassword } from "@/helper/passwordEncryptor";



const router = useRouter();
const authStore = useAuthStore();

const email = ref("");
const password = ref("");
const rememberMe = ref(false);

const isLoading = ref(false);
const error = ref("");

const handleLogin = async () => {
  error.value = "";
  isLoading.value = true;

  try {
    const encryptedPass = await encryptPassword(password.value);
    const response = await authStore.login(email.value, encryptedPass);
    if (response) {
      router.push("/");
    }
  } catch (err: any) { 
    const data = err.response?.data;
    if (data?.errors) {
      const errorKeys = Object.keys(data.errors);
      
      if (errorKeys.length > 0) {
        error.value = data.errors[errorKeys[0]][0];
      } else {
        error.value = data.title || "Validation failed.";
      }
    } else if (data?.message) {
      error.value = data.message;
    }else {
      error.value = "An unexpected error occurred.";
    }
    
  } finally {
    isLoading.value = false;
  }
};

const handleForgotPassword = () => {
  router.push("/forgotpassword");
};
</script>

<template>
  <div class="flex min-h-screen items-center justify-center bg-gray-50 p-4">
    <div class="absolute right-4 top-0 z-50">
      <img
        src="/src/assets/img/ram-logo.png"
        alt="Ram Logo"
        class="h-16 w-auto"
      />
    </div>

    <div class="w-full max-w-md">
      <div class="rounded-2xl bg-white p-8 shadow-lg">
        <div class="mb-8 text-center">
          <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
            <img
              src="/src/assets/img/sb_logo.png"
              alt="SB Logo"
              class="object-contain"
            />
          </div>
          <h1 class="text-2xl font-bold text-ram-red">SBSA Pick Up Point</h1>
          <p class="mt-2 text-gray-500">Sign in to manage your parcels</p>
        </div>

        <form @submit.prevent="handleLogin" class="space-y-5">
          <div
            v-if="error"
            class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {{ error }}
          </div>

           <FormKit 
            v-model="email"
            type="email"
            label="Email Address"
            autocomplete="username"
            placeholder="you@company.com"
             validation="required"
            :classes="{
              messages: 'text-red-600 text-xs mt-1',
              inputInvalid: 'border-red-500 focus:ring-red-500',
            }"
            label-class="block text-sm font-medium text-gray-700 mb-1"
            input-class="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent"

          />

          <FormKit 
            v-model="password"
            type="password"
            label="Password"
            autocomplete="current-password"
            placeholder="Enter your password"
             validation="required|length:8"
            :classes="{
              messages: 'text-red-600 text-xs mt-1',
              inputInvalid: 'border-red-500 focus:ring-red-500',
            }"
            label-class="block text-sm font-medium text-gray-700 mb-1"
            input-class="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent"

          />

          <div class="flex items-center justify-end">
            <!-- <label class="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                v-model="rememberMe"
                class="h-4 w-4 rounded border-gray-300 text-sbsa-blue focus:ring-sbsa-blue"
              />
              <span class="text-sm text-gray-600">Remember me</span>
            </label> -->
            <button
              type="button"
              @click="handleForgotPassword"
              class="text-sm text-ram-red hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <div class="flex gap-3">
            <Button
              type="submit"
              variant="ram"
              :loading="isLoading"
              class="flex-1 bg-ram-red text-white"
            >
              Sign In
            </Button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
